import { useEffect, useState } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate, useParams } from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import { NewCreative } from "./pages/NewCreative";
import { Queue } from "./pages/Queue";
import { Gallery } from "./pages/Gallery";
import { EditKreatif } from "./pages/EditKreatif";
import { ProductSettings } from "./pages/ProductSettings";
import { AppSettings } from "./pages/AppSettings";
import { Login } from "./pages/Login";
import { api, getToken, clearToken } from "./api";
import type { Product } from "@acs/shared";

export default function App() {
  const [authed, setAuthed] = useState<boolean>(() => !!getToken());

  if (!authed) {
    return <Login onLogin={() => setAuthed(true)} />;
  }
  return <AuthedApp onLogout={() => { clearToken(); setAuthed(false); }} />;
}

function AuthedApp({ onLogout }: { onLogout: () => void }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  async function reload() {
    const res = await api.listProducts();
    setProducts(res.products);
  }

  useEffect(() => {
    reload().finally(() => setLoading(false));
    const onChanged = () => reload();
    window.addEventListener("products:changed", onChanged);
    return () => window.removeEventListener("products:changed", onChanged);
  }, []);

  return (
    <div className="flex h-screen">
      <Sidebar products={products} onChanged={reload} onLogout={onLogout} />
      <main className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-zinc-500">Yükleniyor…</div>
        ) : (
          <Routes>
            <Route path="/" element={<Home products={products} />} />
            <Route path="/products/:productId/*" element={<ProductLayout />} />
            <Route path="/settings" element={<AppSettings />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function Home({ products }: { products: Product[] }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (products[0]) navigate(`/products/${products[0].id}/new`, { replace: true });
  }, [products, navigate]);
  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">AI Creative Station</h1>
      <p className="text-zinc-600 mt-2">Soldan bir ürün seçin veya yeni ürün ekleyin.</p>
    </div>
  );
}

function ProductLayout() {
  const { productId } = useParams();
  if (!productId) return null;
  return (
    <div className="flex flex-col h-full">
      <nav className="flex border-b bg-white">
        {[
          { to: `/products/${productId}/new`, label: "Yeni Kreatif" },
          { to: `/products/${productId}/edit`, label: "Edit Kreatif" },
          { to: `/products/${productId}/queue`, label: "Üretim Kuyruğu" },
          { to: `/products/${productId}/gallery`, label: "Galeri" },
          { to: `/products/${productId}/settings`, label: "Ürün Ayarları" },
        ].map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              `px-5 py-3 text-sm font-medium border-b-2 ${
                isActive
                  ? "border-accent text-accent"
                  : "border-transparent text-zinc-600 hover:text-zinc-900"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <div className="flex-1 overflow-y-auto">
        <Routes>
          <Route path="new" element={<NewCreative productId={productId} />} />
          <Route path="edit" element={<EditKreatif productId={productId} />} />
          <Route path="queue" element={<Queue productId={productId} />} />
          <Route path="gallery" element={<Gallery productId={productId} />} />
          <Route path="settings" element={<ProductSettings productId={productId} />} />
          <Route path="*" element={<Navigate to="new" replace />} />
        </Routes>
      </div>
    </div>
  );
}
